<template>
  <div class="br-loading" :class="[`br-loading--${variant}`, { 'br-loading--preview': showPreview }]">
    <div class="br-loading__panel">
      <div class="br-loading__orb br-loading__orb--one" />
      <div class="br-loading__orb br-loading__orb--two" />

      <div class="br-loading__visual">
        <div class="br-loading__ring" aria-hidden="true">
          <span class="br-loading__ring-track" />
          <span class="br-loading__ring-spinner" />
        </div>
        <img
          :src="logoUrl"
          alt="BRPoints"
          class="br-loading__logo"
          draggable="false"
        />
      </div>

      <div class="br-loading__copy">
        <p class="br-loading__message">
          {{ message }}
          <span class="br-loading__dots" aria-hidden="true">
            <span /><span /><span />
          </span>
        </p>
        <p v-if="subtitle" class="br-loading__subtitle">{{ subtitle }}</p>
      </div>
    </div>

    <div v-if="showPreview" class="br-loading__preview">
      <div
        v-for="index in previewCount"
        :key="`preview-${index}`"
        class="br-loading__preview-card"
        :style="{ animationDelay: `${index * 0.12}s` }"
      >
        <div class="br-loading__preview-line br-loading__preview-line--wide" />
        <div class="br-loading__preview-line" />
        <div class="br-loading__preview-line br-loading__preview-line--short" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "PageLoading",
  props: {
    variant: {
      type: String,
      default: "page",
      validator: (value: string) => ["page", "fullscreen"].includes(value),
    },
    message: {
      type: String,
      default: "Carregando dados",
    },
    subtitle: {
      type: String,
      default: "Preparando tudo para você",
    },
    showPreview: {
      type: Boolean,
      default: true,
    },
    previewCount: {
      type: Number,
      default: 3,
    },
    logoUrl: {
      type: String,
      default: () => require("@/assets/logo-login.png"),
    },
  },
});
</script>

<style scoped>
.br-loading {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  padding: 12px 0 8px;
}

.br-loading--fullscreen {
  min-height: calc(100vh - 120px);
  justify-content: center;
  padding: 32px 16px;
}

.br-loading--page {
  min-height: 360px;
  justify-content: center;
}

.br-loading__panel {
  position: relative;
  width: min(100%, 420px);
  padding: 36px 28px 32px;
  border-radius: 24px;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(255, 246, 248, 0.96));
  border: 1px solid rgba(229, 24, 61, 0.08);
  box-shadow:
    0 24px 60px rgba(229, 24, 61, 0.08),
    0 8px 24px rgba(31, 36, 63, 0.06);
  overflow: hidden;
  text-align: center;
}

.br-loading__orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(0);
  pointer-events: none;
}

.br-loading__orb--one {
  width: 180px;
  height: 180px;
  top: -90px;
  right: -40px;
  background: radial-gradient(circle, rgba(229, 24, 61, 0.16) 0%, rgba(229, 24, 61, 0) 72%);
  animation: br-float 5s ease-in-out infinite;
}

.br-loading__orb--two {
  width: 140px;
  height: 140px;
  bottom: -70px;
  left: -30px;
  background: radial-gradient(circle, rgba(36, 48, 94, 0.12) 0%, rgba(36, 48, 94, 0) 72%);
  animation: br-float 6s ease-in-out infinite reverse;
}

.br-loading__visual {
  position: relative;
  width: 148px;
  height: 148px;
  margin: 0 auto 28px;
}

.br-loading__ring {
  position: absolute;
  inset: 0;
}

.br-loading__ring-track,
.br-loading__ring-spinner {
  position: absolute;
  inset: 0;
  border-radius: 50%;
}

.br-loading__ring-track {
  border: 3px solid rgba(229, 24, 61, 0.1);
}

.br-loading__ring-spinner {
  border: 3px solid transparent;
  border-top-color: #e5183d;
  border-right-color: rgba(229, 24, 61, 0.45);
  animation: br-spin 1.1s cubic-bezier(0.6, 0.1, 0.3, 0.9) infinite;
}

.br-loading__logo {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  width: 108px;
  max-height: 84px;
  object-fit: contain;
  transform: translate(-50%, -50%);
  animation: br-pulse 2.4s ease-in-out infinite;
}

.br-loading__copy {
  position: relative;
  z-index: 1;
}

.br-loading__message {
  margin: 0;
  color: #1f243f;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.br-loading__subtitle {
  margin: 8px 0 0;
  color: #7d86a8;
  font-size: 14px;
  font-weight: 500;
}

.br-loading__dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 2px;
  vertical-align: baseline;
}

.br-loading__dots span {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #e5183d;
  opacity: 0.35;
  animation: br-dot 1.4s ease-in-out infinite;
}

.br-loading__dots span:nth-child(2) {
  animation-delay: 0.16s;
}

.br-loading__dots span:nth-child(3) {
  animation-delay: 0.32s;
}

.br-loading__preview {
  width: min(100%, 920px);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.br-loading__preview-card {
  padding: 18px;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%);
  border: 1px solid rgba(31, 36, 63, 0.06);
  box-shadow: 0 10px 30px rgba(31, 36, 63, 0.04);
  animation: br-rise 0.8s ease both;
}

.br-loading__preview-line {
  height: 12px;
  border-radius: 999px;
  margin-bottom: 12px;
  background: linear-gradient(
    90deg,
    rgba(229, 24, 61, 0.08) 0%,
    rgba(229, 24, 61, 0.18) 50%,
    rgba(229, 24, 61, 0.08) 100%
  );
  background-size: 220% 100%;
  animation: br-shimmer 1.6s linear infinite;
}

.br-loading__preview-line--wide {
  width: 72%;
}

.br-loading__preview-line--short {
  width: 46%;
  margin-bottom: 0;
}

.br-loading--preview.br-loading--page {
  min-height: 520px;
}

@keyframes br-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes br-pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.04);
  }
}

@keyframes br-dot {
  0%,
  80%,
  100% {
    opacity: 0.28;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

@keyframes br-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

@keyframes br-float {
  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(0, 10px, 0);
  }
}

@keyframes br-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .br-loading__panel {
    padding: 28px 20px 24px;
    border-radius: 20px;
  }

  .br-loading__message {
    font-size: 16px;
  }

  .br-loading--preview .br-loading__preview {
    grid-template-columns: 1fr;
  }
}
</style>
